import React from 'react';
import store from './store';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  init,
  browserTracingIntegration,
  ErrorBoundary,
  thirdPartyErrorFilterIntegration,
} from '@sentry/react';

import App from 'components/App/App';
import { networkErrorMessages } from 'api/utils';

import './styles.scss';
import GlobalStyle from './GlobalStyle';
import CustomThemeProvider from './components/theme';
import { CustomFetchError } from 'api/customFetch';
import { setStore } from 'storeRef';

const REACT_APP_SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const REACT_APP_SENTRY_ENV = process.env.REACT_APP_SENTRY_ENV || 'local';

if (REACT_APP_SENTRY_ENV !== 'local') {
  init({
    allowUrls: [/https:\/\/solve-widget\.forethought\.ai/],

    attachStacktrace: true,

    beforeSend(event, hint) {
      // Ignore errors that don't have a stack trace and errors from <anonymous>
      // files.
      // https://github.com/getsentry/sentry-javascript/issues/3147
      if (
        event.exception?.values?.[0]?.stacktrace?.frames === undefined ||
        event.exception?.values?.[0]?.stacktrace?.frames?.length === 0 ||
        event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename ===
          '<anonymous>'
      ) {
        return null;
      }

      // Ignore errors from Cloudflare's beacon.min.js:
      if (
        event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename?.endsWith(
          'beacon.min.js',
        )
      ) {
        return null;
      }

      const error = hint.originalException;

      // Ignore errors from Zscaler Internet Security:
      if (
        error instanceof CustomFetchError &&
        typeof error.data === 'string' &&
        error.data.includes('Zscaler')
      ) {
        return null;
      }

      // Ignore WebGL extension errors from browsers
      if (
        event.exception?.values?.[0]?.value?.includes(
          'gl.getSupportedExtensions',
        )
      ) {
        return null;
      }

      return event;
    },

    dsn: REACT_APP_SENTRY_DSN,
    environment: REACT_APP_SENTRY_ENV,
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',

      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'grecaptcha is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',

      // Facebook borked
      'fb_xd_fragment',

      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',

      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',

      // Network errors
      ...networkErrorMessages,

      // Other
      'Object Not Found Matching Id',
      'No provider selected for request metamask_logInjectedWeb3Usage',
      'SyntaxError: Invalid or unexpected token',
      'createRoot(...): Target container is not a DOM element.',
      'Blocked a frame with origin "https://solve-widget.forethought.ai" from accessing a cross-origin frame.',
      "undefined is not an object (evaluating 't.tab.customFillData')",
      "undefined is not an object (evaluating 'L.A.getState().system.isHighContrast')",
      "undefined is not an object (evaluating 'g.Ki.getState().records.fill.lastFilledFieldsByRecord')",
      "undefined is not an object (evaluating 'document.getElementsByClassName('hide-icon hidden mce-visual-caret-hidden')[0].remove')",
      'Unhandled entryType.',
      'can\'t redefine non-configurable property "geolocation"',
      /^Minified Redux error/i,
      /^ResizeObserver/,
      /^Failed to execute 'querySelectorAll' on 'Document'/,
      /^Document.querySelectorAll: .* is not a valid selector$/,
      "'' is not a valid selector.",
      'The string did not match the expected pattern.',
      /^Cannot read properties of null \(reading 'sendMessage'\)$/,
      /^undefined is not an object \(evaluating 'ceCurrentVideo.currentTime'\)$/,
      /^WeakMap key undefined must be an object or an unregistered symbol$/,

      // Forethought permissions errors
      'Invalid permissions',
      'Unauthenticated',
      'Invalid token',

      // Forethought errors that already have Opsgenie alerts
      'WorkflowParameter does not match its associated context variable type',
      'Missing context variable(s) in greeting message.',
      'Missing Context Variable',
    ],
    integrations: [
      browserTracingIntegration(),
      thirdPartyErrorFilterIntegration({
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
        filterKeys: ['forethoughtai-solve-ui'],
      }),
    ],
    normalizeDepth: 10,
    tracesSampleRate: 0.001,
  });
}

function initializeApp() {
  const container = document.getElementById('root');

  if (container === null) {
    return;
  }

  const root = createRoot(container);

  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <Provider store={store}>
          <CustomThemeProvider>
            <GlobalStyle />
            <App />
          </CustomThemeProvider>
        </Provider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}

// !Needed for functions that need access to the store outside of React components
setStore(store);

if (
  document.readyState === 'complete' ||
  document.readyState === 'interactive'
) {
  initializeApp();
} else {
  document.addEventListener('DOMContentLoaded', initializeApp);
}
